import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, Languages, Loader2 } from 'lucide-react';
import { SWITCHER_MESSAGES } from '../constants/languages';
import { useLanguage } from '../hooks/useLanguage';
import './LanguageSwitcher.css';

/**
 * Language button with a dropdown of the languages offered by the backend (English always first).
 * It carries the `dont-translate` class, so its own labels are never machine-translated.
 *
 * placement:
 *   'topbar'   (default) sits in the layout, e.g. inside the top bar next to the notifications
 *   'floating' fixed in the top corner of the screen, for pages without a top bar (login)
 */
const LanguageSwitcher = ({ placement = 'topbar' }) => {
    const {
        language, languageInfo, supportedLanguages, languagesStatus, reloadLanguages,
        changeLanguage, translationStatus,
    } = useLanguage();
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);

    const messages = SWITCHER_MESSAGES[language] || SWITCHER_MESSAGES.en;
    const isTranslating = translationStatus.state === 'translating';
    const hasError = translationStatus.state === 'error';
    // Only English so far: the list is still loading, or the backend could not be reached.
    const onlyEnglish = supportedLanguages.length <= 1;

    // Close on outside click and on Escape while the menu is open.
    useEffect(() => {
        if (!open) return undefined;

        const handlePointerDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
        };
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    const handleSelect = (code) => {
        setOpen(false);
        changeLanguage(code);
    };

    const statusIcon = isTranslating ? (
        <Loader2 size={14} className="lang-switcher-spin" aria-hidden="true" />
    ) : hasError ? (
        <AlertTriangle size={14} className="lang-switcher-error-icon" aria-hidden="true" />
    ) : null;

    return (
        <div className={`lang-switcher lang-switcher--${placement} dont-translate`} ref={rootRef}>
            <button
                type="button"
                className="lang-switcher-trigger"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={messages.selectLanguage}
                title={hasError ? messages.unavailable : isTranslating ? messages.translating : messages.selectLanguage}
                onClick={() => setOpen((value) => !value)}
            >
                <Languages size={16} className="lang-switcher-icon" aria-hidden="true" />
                <span className="lang-switcher-current" lang={languageInfo.code}>
                    {languageInfo.label}
                </span>
                {statusIcon}
                <ChevronDown size={14} className={`lang-switcher-chevron${open ? ' open' : ''}`} aria-hidden="true" />
            </button>

            {open && (
                <div className="lang-switcher-menu" role="menu" aria-label={messages.selectLanguage}>
                    {supportedLanguages.map((item) => {
                        const selected = item.code === language;
                        return (
                            <button
                                key={item.code}
                                type="button"
                                role="menuitemradio"
                                aria-checked={selected}
                                lang={item.code}
                                className={`lang-switcher-option${selected ? ' selected' : ''}`}
                                onClick={() => handleSelect(item.code)}
                            >
                                <span>{item.label}</span>
                                {selected && <Check size={14} aria-hidden="true" />}
                            </button>
                        );
                    })}

                    {onlyEnglish && languagesStatus === 'loading' && (
                        <p className="lang-switcher-state" role="status">{messages.loadingLanguages}</p>
                    )}
                    {onlyEnglish && languagesStatus === 'error' && (
                        <div className="lang-switcher-state error" role="alert">
                            <span>{messages.languagesUnavailable}</span>
                            <button type="button" className="lang-switcher-retry" onClick={reloadLanguages}>
                                {messages.retry}
                            </button>
                        </div>
                    )}

                    {messages.notice && <p className="lang-switcher-notice">{messages.notice}</p>}
                </div>
            )}

            {/* Announced to screen readers; the visible state is the icon in the button. */}
            <span className="lang-switcher-sr" role="status" aria-live="polite">
                {hasError ? messages.unavailable : isTranslating ? messages.translating : ''}
            </span>
        </div>
    );
};

export default LanguageSwitcher;
